import { Col, Row } from "react-grid-system";
import { CMInfoSecurity } from "../../../../lib/api/models";
import { convertDateStringToMonthDayYearFormat } from "../../../../lib/formatting";
import LabeledInfo from "../../../common/LabeledInfo";
import { CMInfoFormInput, CMInfoSubtool } from "../../CMInfo.hooks";
import Collapsible from "../../../common/Collapsible";
import LabeledCheckbox from "../LabeledCheckbox";
import styles from "./CMInfoSecuritySubtool.module.css";
import CMInfoSubtoolButtons from "../CMInfoSubtoolButtons";
import { useState } from "react";

const strings = {
  personalSecurityInfo: "Personal / Security Information",
  lastVerifiedDate: "Last Verified Date",
  nullDate: "XX/XX/XXXX",
  none: "None",
  ssnLast4: "SSN (last 4)",
  securityWord: "Security Word",
  dob: "DOB",
  securityHint: "Security Hint",
  mmn: "MMN",
  reset: "Reset",
  update: "Update",
};

type Props = {
  onShowConfirmationModal: (
    variant: "reset" | "clear",
    onSuccess: () => void,
  ) => void;
  formState: CMInfoFormInput[CMInfoSubtool.Security];
  initialFormState: CMInfoFormInput[CMInfoSubtool.Security];
  generalDispatch: (
    payload: Partial<CMInfoFormInput[CMInfoSubtool.Security]>,
  ) => void;
  security: CMInfoSecurity;
  onUpdate: () => void;
  currentlyUpdating: CMInfoSubtool | "ALL" | null;
  className?: string;
};

export default function CMInfoSecuritySubtool({
  onShowConfirmationModal,
  formState,
  initialFormState,
  generalDispatch,
  security,
  onUpdate,
  currentlyUpdating,
  className,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(true);

  const isDirty =
    formState.isDateOfBirthVerified ||
    formState.isMothersMaidenNameVerified ||
    formState.isSsnLast4Verified ||
    formState.isSecurityVerified;

  return (
    <Collapsible
      disabled={
        formState.securityDueForVerification ||
        formState.dateOfBirthDueForVerification ||
        formState.mothersMaidenNameDueForVerification ||
        formState.ssnLast4DueForVerification ||
        isDirty
      }
      headerText={strings.personalSecurityInfo}
      isExpanded={isExpanded}
      onClick={() => setIsExpanded(!isExpanded)}
      className={className}
    >
      <Row className={styles.mb18}>
        <Col md={2}>
          <LabeledInfo
            label={strings.ssnLast4}
            value={security.ssnLast4 ? security.ssnLast4 : strings.none}
            orientation="vertical"
          />
        </Col>
        <Col offset={{ md: 0.2 }} md={3}>
          <LabeledCheckbox
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.ssnLast4LastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isSsnLast4Verified}
            onCheck={(isSsnLast4Verified) =>
              generalDispatch({
                isSsnLast4Verified,
              })
            }
            isExpired={formState.ssnLast4DueForVerification}
          />
        </Col>
        <Col offset={{ md: 0.1 }} md={2}>
          <LabeledInfo
            label={strings.securityWord}
            value={security.securityWord ? security.securityWord : strings.none}
            orientation="vertical"
          />
        </Col>
        <Col>
          <LabeledCheckbox
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.securityLastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isSecurityVerified}
            onCheck={(isSecurityVerified) =>
              generalDispatch({
                isSecurityVerified,
              })
            }
            isExpired={formState.securityDueForVerification}
          />
        </Col>
      </Row>
      <Row className={styles.mb18}>
        <Col md={2}>
          <LabeledInfo
            label={strings.dob}
            value={convertDateStringToMonthDayYearFormat(security.dateOfBirth)}
            orientation="vertical"
          />
        </Col>
        <Col offset={{ md: 0.2 }} md={3}>
          <LabeledCheckbox
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.dateOfBirthLastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isDateOfBirthVerified}
            onCheck={(isDateOfBirthVerified) =>
              generalDispatch({
                isDateOfBirthVerified,
              })
            }
            isExpired={formState.dateOfBirthDueForVerification}
          />
        </Col>
        <Col offset={{ md: 0.1 }}>
          <LabeledInfo
            label={strings.securityHint}
            value={security.securityHint ? security.securityHint : strings.none}
            orientation="vertical"
          />
        </Col>
      </Row>
      <Row className={styles.mb3}>
        <Col md={2.2} className={styles.wrap}>
          <LabeledInfo
            label={strings.mmn}
            value={
              security.mothersMaidenName
                ? security.mothersMaidenName
                : strings.none
            }
            orientation="vertical"
          />
        </Col>
        <Col>
          <LabeledCheckbox
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.mothersMaidenNameLastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isMothersMaidenNameVerified}
            onCheck={(isMothersMaidenNameVerified) =>
              generalDispatch({
                isMothersMaidenNameVerified,
              })
            }
            isExpired={formState.mothersMaidenNameDueForVerification}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <CMInfoSubtoolButtons
            hideScriptButton
            hideClearButton
            onReset={() =>
              onShowConfirmationModal("reset", () =>
                generalDispatch(initialFormState),
              )
            }
            isResetDisabled={!isDirty}
            onUpdate={onUpdate}
            isUpdateDisabled={!isDirty || !!currentlyUpdating}
            isUpdateLoading={currentlyUpdating === CMInfoSubtool.Security}
          />
        </Col>
      </Row>
    </Collapsible>
  );
}
